import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiStarFill, PiStarLight } from "react-icons/pi";
import client from "../../api/client";

const FEATURE_LABELS = {
  job_board: "Job Board",
  cv_review: "CV Review",
};

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5 text-[#F05A19]">
      {[1, 2, 3, 4, 5].map((n) =>
        n <= rating ? <PiStarFill key={n} size={14} /> : <PiStarLight key={n} size={14} />
      )}
    </div>
  );
}

function SummaryCard({ summary }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      {summary.map((row) => (
        <div key={row.feature} className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-sm font-semibold text-gray-700">{row.label}</p>
          <div className="flex items-end gap-2 mt-2">
            <span className="text-2xl font-bold text-gray-900">{row.avg_rating ?? "—"}</span>
            {row.avg_rating !== null && <Stars rating={Math.round(row.avg_rating)} />}
          </div>
          <p className="text-xs text-gray-400 mt-1">{row.count} response{row.count === 1 ? "" : "s"}</p>
        </div>
      ))}
    </div>
  );
}

function FeedbackRow({ item }) {
  const name = `${item.firstname || ""} ${item.lastname || ""}`.trim() || item.email;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{name}</span>
            <span className="text-xs text-gray-400">{item.email}</span>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
              {FEATURE_LABELS[item.feature] || item.feature}
            </span>
          </div>
          <div className="mt-1.5"><Stars rating={item.rating} /></div>
        </div>
        <span className="text-xs text-gray-300">{new Date(item.created_at).toLocaleString()}</span>
      </div>
      {item.comment && (
        <p className="text-sm text-gray-700 mt-3 leading-relaxed">{item.comment}</p>
      )}
    </div>
  );
}

export default function AdminFeedbackPage() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [feature, setFeature] = useState("");
  const [rating, setRating] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    client.get("/admin/feature-feedback/summary").then((res) => setSummary(res.data || [])).catch(() => {});
  }, []);

  const load = useCallback((p = 1) => {
    setLoading(true);
    client
      .get("/admin/feature-feedback", { params: { search, feature, rating, page: p } })
      .then((res) => {
        setItems(res.data.data || []);
        setMeta(res.data);
        setPage(p);
      })
      .catch(() => toast.error("Failed to load feedback."))
      .finally(() => setLoading(false));
  }, [search, feature, rating]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Feature Feedback</h1>
        <p className="text-sm text-gray-500 mt-0.5">Student ratings and comments on the Job Board and CV Review.</p>
      </div>

      {summary.length > 0 && <SummaryCard summary={summary} />}

      <div className="flex items-center gap-3 flex-wrap mb-5">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, or comment…"
          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-64"
        />
        <select
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30"
        >
          <option value="">All features</option>
          {Object.entries(FEATURE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={rating}
          onChange={(e) => setRating(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30"
        >
          <option value="">Any rating</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>
          ))}
        </select>
      </div>

      <div className="space-y-3">
        {loading ? (
          [1, 2, 3, 4].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <p className="text-4xl mb-3">⭐</p>
            <p className="text-sm font-medium text-gray-600">No feedback yet</p>
          </div>
        ) : (
          items.map((item) => <FeedbackRow key={item.id} item={item} />)
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      )}
    </div>
  );
}
